class FPProductSalon extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';

    this.variants = JSON.parse(this.querySelector('[data-fp-variants]')?.textContent || '[]');
    this.sizeProfile = JSON.parse(this.querySelector('[data-size-guide-data]')?.textContent || '{}').profile;
    this.isPriceOnRequest = this.dataset.priceOnRequest === 'true';
    this.colorIndex = Number(this.dataset.colorPosition || 0) - 1;
    this.sizeIndex = Number(this.dataset.sizePosition || 0) - 1;
    this.form = this.querySelector('form');
    this.addButton = this.querySelector('[ref="addToCartButton"]');
    this.optionInputs = [...this.querySelectorAll('[data-option-position] input[type="radio"]')];
    this.mediaGrid = this.querySelector('[data-fp-media-grid]');
    this.thumbnailRail = this.querySelector('[data-fp-media-thumbnails]');
    this.mediaControls = this.querySelector('[data-fp-media-controls]');
    this.previousMediaButton = this.querySelector('[data-fp-media-previous]');
    this.nextMediaButton = this.querySelector('[data-fp-media-next]');
    this.quantityInput = this.querySelector('[data-fp-quantity]');
    this.quantityMinusButton = this.querySelector('[data-fp-quantity-minus]');
    this.quantityPlusButton = this.querySelector('[data-fp-quantity-plus]');
    this.stickyAdd = this.querySelector('[data-fp-sticky-add]');
    this.zoomDialog = this.querySelector('[data-fp-zoom-dialog]');
    this.zoomImage = this.querySelector('[data-fp-zoom-image]');
    this.zoomButtons = [...this.querySelectorAll('[data-fp-media-zoom]')];
    this.zoomClose = this.querySelector('[data-fp-zoom-close]');
    this.primaryButtonPassed = false;
    this.swipeStartX = null;

    this.optionInputs.forEach((input) => input.addEventListener('change', (event) => this.onOptionChange(event)));
    this.previousMediaButton?.addEventListener('click', () => this.moveMedia(-1));
    this.nextMediaButton?.addEventListener('click', () => this.moveMedia(1));
    this.quantityMinusButton?.addEventListener('click', () => this.changeQuantity(-1));
    this.quantityPlusButton?.addEventListener('click', () => this.changeQuantity(1));
    this.quantityInput?.addEventListener('input', () => this.normalizeQuantity());
    this.stickyAdd?.addEventListener('click', () => this.form?.requestSubmit());
    this.zoomButtons.forEach((button) => button.addEventListener('click', () => this.openZoom(button)));
    this.zoomClose?.addEventListener('click', () => this.zoomDialog?.close());
    this.zoomDialog?.addEventListener('click', (event) => {
      if (event.target === this.zoomDialog) this.zoomDialog.close();
    });
    this.mediaGrid?.addEventListener('pointerdown', (event) => this.startSwipe(event));
    this.mediaGrid?.addEventListener('pointerup', (event) => this.endSwipe(event));
    document.addEventListener('toggle', () => this.syncStickyVisibility(), true);

    this.update(this.variantFromId(this.querySelector('[data-fp-variant-id]')?.value));
    this.normalizeQuantity();
    this.observePrimaryButton();
  }

  startSwipe(event) {
    if (event.pointerType === 'mouse') return;
    this.swipeStartX = event.clientX;
  }

  endSwipe(event) {
    if (this.swipeStartX === null) return;
    const distance = event.clientX - this.swipeStartX;
    this.swipeStartX = null;
    if (Math.abs(distance) < 45) return;
    this.moveMedia(distance > 0 ? -1 : 1);
  }

  openZoom(button) {
    const source = button.closest('[data-fp-media]')?.querySelector('img');
    if (!source || !this.zoomDialog || !this.zoomImage) return;
    this.zoomImage.src = source.currentSrc || source.src;
    this.zoomImage.alt = source.alt || '';
    this.zoomDialog.showModal();
  }

  changeQuantity(change) {
    if (!this.quantityInput) return;
    const current = Number.parseInt(this.quantityInput.value, 10) || 1;
    this.quantityInput.value = String(current + change);
    this.normalizeQuantity();
  }

  normalizeQuantity() {
    if (!this.quantityInput) return;
    const minimum = Number.parseInt(this.quantityInput.min, 10) || 1;
    const maximum = Number.parseInt(this.quantityInput.max, 10) || 99;
    const value = Math.min(maximum, Math.max(minimum, Number.parseInt(this.quantityInput.value, 10) || minimum));
    this.quantityInput.value = String(value);
    if (this.quantityMinusButton) this.quantityMinusButton.disabled = value <= minimum;
    if (this.quantityPlusButton) this.quantityPlusButton.disabled = value >= maximum;
  }

  variantFromId(id) {
    return this.variants.find((variant) => String(variant.id) === String(id));
  }

  selectedOptions() {
    return [...this.querySelectorAll('[data-option-position]')]
      .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
      .map((fieldset) => fieldset.querySelector('input:checked')?.value || '');
  }

  findExactVariant(options) {
    return this.variants.find((variant) => variant.options.every((value, index) => value === options[index]));
  }

  onOptionChange(event) {
    let options = this.selectedOptions();
    let variant = this.findExactVariant(options);

    const fieldset = event.target.closest('[data-option-position]');
    if ((!variant || !variant.available) && fieldset?.hasAttribute('data-color-option') && this.colorIndex >= 0) {
      variant = this.variants.find((candidate) => candidate.available && candidate.options[this.colorIndex] === event.target.value);
      if (variant) {
        this.syncInputs(variant.options);
        options = variant.options;
      }
    }

    this.update(variant || this.findExactVariant(options));
  }

  syncInputs(options) {
    [...this.querySelectorAll('[data-option-position]')].forEach((fieldset) => {
      const index = Number(fieldset.dataset.optionPosition) - 1;
      const input = [...fieldset.querySelectorAll('input')].find((candidate) => candidate.value === options[index]);
      if (input) input.checked = true;
    });
  }

  update(variant) {
    if (!variant) return;

    const variantInput = this.querySelector('[data-fp-variant-id]');
    if (variantInput) variantInput.value = variant.id;
    const selectedSize = this.sizeProfile?.sizeMap?.find(row => row.native === variant.options[this.sizeIndex]);
    let usProperty = this.form?.querySelector('[name="properties[US size]"]');
    if (selectedSize?.us && this.form) {
      if (!usProperty) { usProperty = document.createElement('input'); usProperty.type = 'hidden'; usProperty.name = 'properties[US size]'; this.form.appendChild(usProperty); }
      usProperty.value = selectedSize.us;
    } else usProperty?.remove();

    const price = this.querySelector('[data-fp-price]');
    const compare = this.querySelector('[data-fp-compare]');
    const availability = this.querySelector('[data-fp-availability]');
    const stickyPrice = this.querySelector('[data-fp-sticky-price]');
    const stickyVariant = this.querySelector('[data-fp-sticky-variant]');
    const stickyLabel = this.querySelector('[data-fp-sticky-label]');
    const stickyImage = this.querySelector('.fp-house-salon__sticky-image');
    if (price) price.textContent = this.isPriceOnRequest ? 'Price on request' : variant.price;
    if (stickyPrice) stickyPrice.textContent = this.isPriceOnRequest ? 'Price on request' : variant.price;
    if (compare) {
      compare.textContent = variant.compareAt;
      compare.hidden = !variant.compareAt || variant.compareAt === variant.price;
    }
    if (availability) {
      availability.lastChild.textContent = this.isPriceOnRequest ? 'Available by inquiry' : (variant.available ? 'Available now' : 'Currently unavailable');
      availability.classList.toggle('is-unavailable', !variant.available && !this.isPriceOnRequest);
    }
    if (this.addButton) {
      this.addButton.disabled = !variant.available;
      const textNode = [...this.addButton.childNodes].find((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
      if (textNode) textNode.textContent = variant.available ? 'Add to bag' : 'Sold out';
    }
    if (stickyVariant) {
      const optionLabel = variant.options.map((value, index) => index === this.sizeIndex ? (selectedSize?.display || value) : value).filter((value) => value && value !== 'Default Title').join(' / ');
      stickyVariant.textContent = optionLabel;
      stickyVariant.hidden = !optionLabel;
    }
    if (stickyImage && variant.image) stickyImage.src = variant.image;
    if (this.stickyAdd) this.stickyAdd.disabled = !variant.available;
    if (stickyLabel) stickyLabel.textContent = variant.available ? 'Add to bag' : 'Sold out';
    this.syncStickyVisibility();

    const selectedColor = this.colorIndex >= 0 ? variant.options[this.colorIndex] : '';
    const selectedColorLabel = this.querySelector('[data-fp-selected-color]');
    if (selectedColorLabel) selectedColorLabel.textContent = selectedColor;
    this.updateSizeAvailability(selectedColor);
    this.updateMedia(selectedColor);

    const url = new URL(this.dataset.productUrl, window.location.origin);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({ variantId: variant.id }, '', `${url.pathname}${url.search}`);
  }

  updateSizeAvailability(selectedColor) {
    const sizeFieldset = this.querySelector('[data-size-option]');
    if (!sizeFieldset || this.sizeIndex < 0) return;

    sizeFieldset.querySelectorAll('input').forEach((input) => {
      const available = this.variants.some((variant) => {
        const matchesColor = this.colorIndex < 0 || variant.options[this.colorIndex] === selectedColor;
        return matchesColor && variant.options[this.sizeIndex] === input.value && variant.available;
      });
      input.disabled = !available;
      input.setAttribute('aria-disabled', String(!available));
      input.closest('[data-fp-option-value]')?.classList.toggle('is-unavailable', !available);
    });
  }

  mediaColor(item) {
    if (item.dataset.fpColor) return item.dataset.fpColor;
    const marker = item.dataset.fpSourceAlt?.match(/\[fp-color:([^\]]+)\]/i);
    return marker ? this.handleize(marker[1]) : '';
  }

  handleize(value) {
    return String(value || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  updateMedia(selectedColor) {
    if (!this.mediaGrid) return;
    const items = [...this.mediaGrid.querySelectorAll('[data-fp-media]')];
    const colorKey = this.handleize(selectedColor);
    const hasMappedMedia = items.some((item) => this.mediaColor(item));

    items.forEach((item) => {
      const itemColor = this.mediaColor(item);
      const shared = item.dataset.fpShared === 'true';
      item.hidden = hasMappedMedia && !(shared || !itemColor || itemColor === colorKey);
      item.classList.remove('is-active');
      item.removeAttribute('aria-hidden');
    });

    let visible = items.filter((item) => !item.hidden);
    if (!visible.length) {
      items.forEach((item) => { item.hidden = false; });
      visible = items;
    }

    this.setMediaStage(visible, 0);
  }

  setMediaStage(visible, activeIndex) {
    visible.forEach((item) => item.classList.remove('is-active'));
    if (!visible.length) return;

    const normalizedIndex = Math.max(0, Math.min(activeIndex, visible.length - 1));
    this.visibleMedia = visible;
    this.activeMediaIndex = normalizedIndex;
    visible.forEach((item, index) => {
      const isActive = index === normalizedIndex;
      item.classList.toggle('is-active', isActive);
      item.setAttribute('aria-hidden', String(!isActive));
    });
    this.renderThumbnails(visible, normalizedIndex);
    this.updateMediaControls();
  }

  moveMedia(direction) {
    if (!this.visibleMedia?.length) return;
    this.setMediaStage(this.visibleMedia, this.activeMediaIndex + direction);
  }

  updateMediaControls() {
    const total = this.visibleMedia?.length || 0;
    const current = this.querySelector('[data-fp-media-current]');
    const totalLabel = this.querySelector('[data-fp-media-total]');
    if (current) current.textContent = total ? this.activeMediaIndex + 1 : 0;
    if (totalLabel) totalLabel.textContent = total;
    if (this.mediaControls) this.mediaControls.hidden = total <= 1;
    if (this.previousMediaButton) this.previousMediaButton.disabled = this.activeMediaIndex <= 0;
    if (this.nextMediaButton) this.nextMediaButton.disabled = this.activeMediaIndex >= total - 1;
  }

  renderThumbnails(visible, selectedIndex) {
    if (!this.thumbnailRail) return;
    this.thumbnailRail.replaceChildren();

    visible.forEach((item, index) => {
      const sourceImage = item.querySelector('img');
      if (!sourceImage) return;
      const button = document.createElement('button');
      const image = document.createElement('img');
      button.type = 'button';
      button.className = 'fp-house-salon__thumbnail';
      button.setAttribute('aria-label', `Show product view ${index + 1}`);
      button.setAttribute('aria-pressed', String(index === selectedIndex));
      image.src = sourceImage.currentSrc || sourceImage.src;
      image.alt = '';
      image.loading = 'lazy';
      button.appendChild(image);
      button.addEventListener('click', () => this.setMediaStage(visible, index));
      this.thumbnailRail.appendChild(button);
    });
  }

  observePrimaryButton() {
    if (!this.stickyAdd || !this.addButton || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(([entry]) => {
      this.primaryButtonPassed = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
      this.syncStickyVisibility();
    });
    observer.observe(this.addButton);
  }

  syncStickyVisibility() {
    if (!this.stickyAdd) return;
    const cartDialog = document.querySelector('#cart-drawer dialog');
    const cartOpen = Boolean(cartDialog?.open || cartDialog?.matches('[open]'));
    this.stickyAdd.hidden = !this.primaryButtonPassed || Boolean(this.addButton?.disabled) || cartOpen;
  }
}

if (!customElements.get('fp-house-product-salon')) {
  customElements.define('fp-house-product-salon', FPProductSalon);
}
